import React, { useEffect, useRef, useState } from "react";
import Slider from "react-slick";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { cachedFetchJson } from "../../utils/apiCache";

const getVideoId = (url) => {
  if (!url) return "";
  try {
    const parsed = new URL(url);
    if (parsed.pathname.startsWith("/shorts/")) {
      return parsed.pathname.split("/shorts/")[1]?.split("/")[0] || "";
    }
    if (parsed.searchParams.get("v")) return parsed.searchParams.get("v");
    const parts = parsed.pathname.split("/");
    return parts[parts.length - 1] || "";
  } catch {
    if (url.includes("shorts/")) return url.split("shorts/")[1]?.split("?")[0] || "";
    if (url.includes("watch?v=")) return url.split("watch?v=")[1]?.split("&")[0] || "";
    return "";
  }
};

const getPosterUrl = (videoId) =>
  videoId ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` : "";

const EMBED_HOST = "https://www.youtube.com";

const ReelCard = ({
  reel,
  className,
  iframeRefs,
  registerPlayer,
  handleSoundToggle,
  soundVideoId,
  embedOrigin,
  isActive,
  onPlay,
}) => {
  const videoId = getVideoId(reel.url);
  const soundOn = soundVideoId === reel.id;
  const poster = getPosterUrl(videoId);

  return (
    <div className={className}>
      <div className="relative aspect-[9/16] w-full overflow-hidden rounded-2xl bg-black">
        {videoId && isActive ? (
          <iframe
            ref={(node) => {
              if (node) iframeRefs.current[reel.id] = node;
              else delete iframeRefs.current[reel.id];
            }}
            onLoad={() => registerPlayer(reel.id)}
            className="absolute inset-0 h-full w-full"
            src={`${EMBED_HOST}/embed/${videoId}?autoplay=1&mute=1&controls=0&modestbranding=1&rel=0&playsinline=1&enablejsapi=1&origin=${encodeURIComponent(
              embedOrigin,
            )}`}
            title="YouTube Short"
            frameBorder="0"
            allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        ) : videoId ? (
          <button
            type="button"
            onClick={() => onPlay(reel.id)}
            className="absolute inset-0 w-full h-full"
            aria-label={`Play video ${reel.id}`}
          >
            <img
              src={poster}
              alt="Video preview"
              className="h-full w-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/30" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90 text-[#351F72] shadow-lg">
                <i className="fa-solid fa-play"></i>
              </span>
            </div>
          </button>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500">
            Invalid video URL
          </div>
        )}

        {isActive && (
          <button
            type="button"
            onClick={() => handleSoundToggle(reel.id)}
            className="absolute bottom-2 right-2 sm:bottom-3 sm:right-3 flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur transition hover:bg-black/75"
            aria-label={soundOn ? "Mute video" : "Unmute video"}
          >
            <i className={`fa-solid ${soundOn ? "fa-volume-high" : "fa-volume-xmark"}`}></i>
          </button>
        )}
      </div>
    </div>
  );
};

const WatchAndShop = () => {
  const [reels, setReels] = useState([]);
  const [activeVideoId, setActiveVideoId] = useState(null);
  const [soundVideoId, setSoundVideoId] = useState(null);
  const iframeRefs = useRef({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const originRef = useRef("");

  const settings = {
    dots: true,
    infinite: false,
    speed: 600,
    slidesToShow: 5,
    slidesToScroll: 1,
    swipeToSlide: true,
    responsive: [
      { breakpoint: 1280, settings: { slidesToShow: 3 } },
      { breakpoint: 768, settings: { slidesToShow: 2 } },
      { breakpoint: 480, settings: { slidesToShow: 2 } },
    ],
  };

  useEffect(() => {
    if (typeof window !== "undefined") originRef.current = window.location.origin;
  }, []);

  useEffect(() => {
    let cancelled = false;
    let requestUrl = "";

    const loadReels = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const backendRoot = process.env.REACT_APP_BACKEND;
        const apiBase = process.env.REACT_APP_API_BASE_URL;
        const baseUrl = (backendUrl || apiBase || backendRoot || "http://localhost:5000").replace(
          /\/$/,
          "",
        );
        const defaultPath = backendUrl ? "/live-videos" : "/api/live-videos";
        const path = process.env.REACT_APP_LIVE_VIDEOS_PATH || defaultPath;
        const params = new URLSearchParams({ status: "active", limit: "12" });
        requestUrl = `${baseUrl}${path}?${params.toString()}`;
        const data = await cachedFetchJson(requestUrl, { ttlMs: 120000 });
        const dataArray = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : [];
        const normalized = dataArray
          .map((item) => ({
            id: item.id,
            url: item.youtube_url || "",
            status: item.status || "active",
          }))
          .filter((item) => item.url);

        if (cancelled) return;
        setReels(normalized);
      } catch (err) {
        if (!cancelled) {
          console.error("Error fetching reels:", err);
          setError(err.message || "Unable to load videos");
          setReels([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadReels();

    return () => {
      cancelled = true;
    };
  }, []);

  const sendCommand = (id, func) => {
    const iframe = iframeRefs.current[id];
    if (!iframe || !iframe.contentWindow) return;
    iframe.contentWindow.postMessage(
      JSON.stringify({ event: "command", func, args: [] }),
      "*",
    );
  };

  const registerPlayer = (id) => {
    const iframe = iframeRefs.current[id];
    if (!iframe || !iframe.contentWindow) return;
    iframe.contentWindow.postMessage(
      JSON.stringify({ event: "listening", id: String(id) }),
      "*",
    );
  };

  const handleSoundToggle = (id) => {
    if (soundVideoId === id) {
      sendCommand(id, "mute");
      setSoundVideoId(null);
      return;
    }

    Object.keys(iframeRefs.current).forEach((key) => {
      if (String(key) !== String(id)) sendCommand(key, "mute");
    });
    sendCommand(id, "playVideo");
    sendCommand(id, "unMute");
    setSoundVideoId(id);
  };

  const handlePlay = (id) => {
    setActiveVideoId((prev) => (String(prev) === String(id) ? null : id));
  };

  useEffect(() => {
    const handleMessage = (event) => {
      if (
        !event.origin.includes("youtube.com") &&
        !event.origin.includes("youtube-nocookie.com")
      ) {
        return;
      }
      let payload = event.data;
      if (typeof payload === "string") {
        try {
          payload = JSON.parse(payload);
        } catch {
          return;
        }
      }
      if (!payload || payload.event !== "infoDelivery") return;

      const sourceId = Object.keys(iframeRefs.current).find((key) => {
        const frame = iframeRefs.current[key];
        return frame && frame.contentWindow === event.source;
      });

      if (!sourceId) return;
      const isMuted = payload.info?.muted;
      if (typeof isMuted !== "boolean") return;

      if (isMuted === false) {
        Object.keys(iframeRefs.current).forEach((key) => {
          if (String(key) !== String(sourceId)) sendCommand(key, "mute");
        });
        setSoundVideoId(Number(sourceId));
      } else if (soundVideoId === Number(sourceId)) {
        setSoundVideoId(null);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [soundVideoId]);

  useEffect(() => {
    if (!soundVideoId) return undefined;
    const interval = setInterval(() => {
      Object.keys(iframeRefs.current).forEach((key) => {
        if (String(key) !== String(soundVideoId)) sendCommand(key, "mute");
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [soundVideoId]);
  useEffect(() => {
    iframeRefs.current = {};
    setSoundVideoId(null);
  }, [activeVideoId]);

  return (
    <section className="lg:py-14 py-8 px-4">
      <h2 className="text-3xl sm:text-4xl font-extrabold text-[#351F72] tracking-tight lg:mb-5 mb-2">
        Watch and Shop
      </h2>

      {error && (
        <p className="mb-4 text-sm text-red-500" role="alert">
          {error}
        </p>
      )}

      <div className="overflow-visible">
        <style>{`
          .reel-slider .slick-dots {
            position: static;
            margin-top: 16px;
            display: flex !important;
            align-items: center;
            justify-content: center;
            gap: 10px;
          }
          .reel-slider .slick-dots li button:before {
            font-size: 12px;
            color: #2b174a;
            opacity: 0.45;
          }
          .reel-slider .slick-dots li.slick-active button:before {
            opacity: 1;
            color: #ff8a00;
          }
          .reel-slider .slick-dots li {
            margin: 0;
          }
        `}</style>
        {(() => {
          const embedOrigin =
            originRef.current ||
            (typeof window !== "undefined" ? window.location.origin : "");

          return (
            <>
              {/* Mobile: 2 frames visible + horizontal scroll (more reliable than slick for iframes) */}
              <div className="sm:hidden">
                <div
                  className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x snap-mandatory scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                  {reels.map((reel) => (
                    <ReelCard
                      key={reel.id}
                      reel={reel}
                      className="w-[48%] flex-none snap-start"
                      iframeRefs={iframeRefs}
                      registerPlayer={registerPlayer}
                      handleSoundToggle={handleSoundToggle}
                      soundVideoId={soundVideoId}
                      embedOrigin={embedOrigin}
                      isActive={String(activeVideoId) === String(reel.id)}
                      onPlay={handlePlay}
                    />
                  ))}
                </div>
              </div>

              {/* Tablet/Desktop: slick carousel */}
              <div className="hidden sm:block">
                <Slider {...settings} className="reel-slider">
                  {reels.map((reel) => (
                    <ReelCard
                      key={reel.id}
                      reel={reel}
                      className="px-2 md:px-3"
                      iframeRefs={iframeRefs}
                      registerPlayer={registerPlayer}
                      handleSoundToggle={handleSoundToggle}
                      soundVideoId={soundVideoId}
                      embedOrigin={embedOrigin}
                      isActive={String(activeVideoId) === String(reel.id)}
                      onPlay={handlePlay}
                    />
                  ))}
                </Slider>
              </div>
            </>
          );
        })()}
        {!isLoading && reels.length === 0 && (
          <div className="mt-6 text-sm text-gray-500">No videos available.</div>
        )}
      </div>
    </section>
  );
};

export default WatchAndShop;
